/**
 * A deployment's endpoints and identifiers.
 *
 * Read out of the installer's own end-of-run banner, which
 * `print_terraform_output` prints at the end of every successful `apply` and
 * whenever the `output` command is run. The labels below are that script's
 * `printf` labels verbatim — extracted from it, not paraphrased, because a
 * label this file invents matches nothing and finds no outputs at all.
 *
 * ## Why the log and not `terraform output`
 *
 * There is no other path. `terraform-output.yaml` exists only inside the
 * container — gitignored, dockerignored, bind-mounted in the manual flow, and
 * nothing uploads it — so it dies with the ECS task. The state file is in the
 * customer's S3 bucket, which the diagnostics role is explicitly denied
 * `s3:GetObject` on. The banner is what leaves their account, and it leaves it
 * already redacted.
 *
 * ## Sensitive values are never carried, not merely masked
 *
 * Six of the installer's outputs are `sensitive` in Terraform, and the redactor
 * masks them in the customer's account before the line is written. This file
 * refuses to record them **a second time**, on the field rather than on what the
 * line happened to contain: a `sensitive` field is reported as withheld even if
 * the value arrived in the clear. That is deliberate belt-and-braces — a
 * redaction rule that stops matching should cost a support engineer a value, not
 * put a customer's database password in Qrvey's DynamoDB table.
 *
 * That is also why there is no reveal. Recovering one means reading it from the
 * customer's own account, which is where it already is.
 */
import { stateBucketFor } from '../bootstrap/template.js';
import { stateKeyFor } from '../deployment/config.js';
import { awsAccountIdFromAccountKey } from '../tenancy/cloud-account.js';
/**
 * The banner rows worth surfacing.
 *
 * An allow list, which is what makes reading this safe at `deployment:read`
 * rather than `logs:read`: the endpoint returns strictly these fields and never
 * arbitrary log content, so it cannot become a way to read a log without the
 * capability for one.
 *
 * The bare `URL:`, `User:` and `Password:` rows are deliberately absent. They
 * belong to the ArgoCD block, which only prints when
 * `QRVEY_SHOW_ARGOCD_DETAILS=1` — the deploy and adopt banners, not the commands
 * this control plane runs — and a one-word label is ambiguous besides.
 */
export const DEPLOYMENT_OUTPUT_FIELDS = [
    { key: 'environmentUrl', label: 'Environment URL', kind: 'url', sensitive: false },
    { key: 'adminPortalUrl', label: 'Admin Portal URL', kind: 'url', sensitive: false },
    { key: 'adminUser', label: 'Admin User', kind: 'text', sensitive: false },
    { key: 'adminPassword', label: 'Admin Password', kind: 'text', sensitive: true },
    { key: 'qrveyApiKey', label: 'Qrvey API Key', kind: 'text', sensitive: true },
    { key: 'loadBalancerUrl', label: 'Load Balancer URL', kind: 'url', sensitive: false },
    { key: 'migrationLbInfo', label: 'Migration LB Info', kind: 'text', sensitive: false },
    { key: 'postgresConnection', label: 'PostgreSQL Connection', kind: 'text', sensitive: true },
    { key: 'elasticsearchHost', label: 'Elasticsearch Host', kind: 'url', sensitive: false },
    { key: 'elasticsearchUser', label: 'Elasticsearch User', kind: 'text', sensitive: false },
    { key: 'elasticsearchPassword', label: 'Elasticsearch Password', kind: 'text', sensitive: true },
    { key: 'openSearchUrl', label: 'OpenSearch URL', kind: 'url', sensitive: false },
    { key: 'rabbitmqHost', label: 'RabbitMQ Host', kind: 'url', sensitive: false },
    { key: 'rabbitmqUser', label: 'RabbitMQ User', kind: 'text', sensitive: false },
    { key: 'rabbitmqPassword', label: 'RabbitMQ Password', kind: 'text', sensitive: true },
    { key: 'monitoring', label: 'Monitoring', kind: 'text', sensitive: false },
    { key: 'grafanaUrl', label: 'Grafana URL', kind: 'url', sensitive: false },
    { key: 'grafanaUser', label: 'Grafana User', kind: 'text', sensitive: false },
    { key: 'grafanaPassword', label: 'Grafana Password', kind: 'text', sensitive: true },
    { key: 'deploymentId', label: 'Deployment ID', kind: 'text', sensitive: false },
    { key: 'qrveyVersion', label: 'Qrvey Version Deployed', kind: 'text', sensitive: false },
    { key: 'qrveyMode', label: 'Qrvey Mode', kind: 'text', sensitive: false },
];
/**
 * Whether the installer is saying it has no value, rather than giving one.
 *
 * Matched on shape rather than on a list of literals. `<NOT FOUND>` was the only
 * one handled until a real apply produced a second — `<UNAVAILABLE — live LB
 * hostname could not be fetched>`, which fell through and was recorded as a
 * `present` load balancer URL (IM-82). A support engineer then reads an error
 * message where an address should be, and the three states stop carrying the
 * distinction they exist for.
 *
 * Angle brackets around the whole value are safe to key on: no endpoint,
 * version, mode or URL the banner prints is ever written that way, and the
 * installer uses the form precisely to mean "nothing here". Matching the shape
 * also means the next placeholder needs no release.
 */
const isInstallerPlaceholder = (raw) => raw.startsWith('<') && raw.endsWith('>');
/** Any of the redactor's markers. All of them begin this way. */
const REDACTED = '[redacted';
const byLongestLabel = (a, b) => b.label.length - a.label.length;
/**
 * Reads the banner out of a run's log lines.
 *
 * Matches on the whole label followed by a colon at the start of the line, so
 * `Admin User:` cannot be read as the ArgoCD block's bare `User:`. Longest label
 * first for the same reason.
 *
 * A line the allow list does not name is ignored. That is the point: this
 * function's output is bounded by `DEPLOYMENT_OUTPUT_FIELDS`, so it cannot
 * become a general-purpose log reader.
 */
export const parseDeploymentOutputs = (lines, context) => {
    const fields = [...DEPLOYMENT_OUTPUT_FIELDS].sort(byLongestLabel);
    const values = {};
    for (const line of lines) {
        const trimmed = line.trim();
        for (const field of fields) {
            const prefix = `${field.label}:`;
            if (!trimmed.startsWith(prefix))
                continue;
            const raw = trimmed.slice(prefix.length).trim();
            // On the field, not on the value. A `sensitive` output is withheld even if
            // it arrived in the clear, so a redaction rule that stops matching cannot
            // put a customer's password in this control plane's storage.
            if (field.sensitive)
                values[field.key] = { state: 'withheld' };
            else if (raw === '' || isInstallerPlaceholder(raw))
                values[field.key] = { state: 'missing' };
            else if (raw.includes(REDACTED))
                values[field.key] = { state: 'withheld' };
            else
                values[field.key] = { state: 'present', value: raw };
            // Later rows win: `output` reprints the banner, and the last one is the
            // freshest. Break so a longer label does not also match a shorter one.
            break;
        }
    }
    return { fromRunId: context.fromRunId, readAt: context.readAt, values };
};
/** Whether anything at all was found, so a caller can say "not collected yet". */
export const hasOutputs = (outputs) => outputs !== undefined && Object.keys(outputs.values).length > 0;
/**
 * An address a browser can open, or undefined if this is not one.
 *
 * The installer prints some endpoints with a scheme (`http://host:15672/#/`) and
 * some as a bare hostname (an ELB's `a1b2c3.elb.us-east-1.amazonaws.com`). A
 * bare host gets `https://`, because a link that does not work is no better than
 * text and every one of these is served over TLS. Anything without a dot is not
 * a host and stays text.
 */
export const outputLink = (field, value) => {
    if (field.kind !== 'url' || value.state !== 'present')
        return undefined;
    if (/^https?:\/\//i.test(value.value))
        return value.value;
    if (!value.value.includes('.') || /\s/.test(value.value))
        return undefined;
    return `https://${value.value}`;
};
export const stateLocation = (input) => {
    const awsAccountId = awsAccountIdFromAccountKey(input.accountKey);
    if (awsAccountId === undefined || input.region === undefined || input.deploymentId === '') {
        return undefined;
    }
    const bucket = stateBucketFor(awsAccountId, input.region);
    const key = stateKeyFor(input.deploymentId);
    return { bucket, key, uri: `s3://${bucket}/${key}` };
};
/** Whether anything here is the customer's to hold, so a caller can say where. */
export const hasWithheldOutputs = (outputs) => outputs !== undefined && Object.values(outputs.values).some((value) => value.state === 'withheld');
//# sourceMappingURL=outputs.js.map